import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { feefoApi, ApiError } from "@/api";
import type { FeefoReceivedItem } from "@/types";
import { Spinner, Toast, useToast } from "@/components/ui";
import { FeefoRequestRow } from "./feedbackShared";
import { FeedbackRequestFlow } from "./FeedbackRequestFlow";
import "./feedback.css";

/**
 * Feedback › Feedback received — cross-job feed of every review received to date,
 * newest first. Shares the "New feedback request" flow with the Open requests
 * page (both host it so the header button works from either view).
 */
export function FeedbackReceivedPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [items, setItems] = useState<FeefoReceivedItem[] | null>(null);
  const [error, setError] = useState<string | undefined>();
  const [showNew, setShowNew] = useState(false);
  const { toast, showToast } = useToast();

  const load = useCallback(() => {
    let cancelled = false;
    feefoApi
      .listReceivedFeedback()
      .then((res) => {
        if (!cancelled) setItems(res.items);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not load received feedback.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => load(), [load]);

  useEffect(() => {
    if ((location.state as { create?: boolean } | null)?.create) {
      setShowNew(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  const avg =
    items && items.length
      ? items.reduce((s, r) => s + (r.review_rating || 0), 0) / items.length
      : 0;

  return (
    <section className="feedback-page">
      <header className="feedback-page__head">
        <h1>Feedback received</h1>
        <p className="feefo__blurb">Every review left by a client, newest first.</p>
        {items && items.length > 0 ? (
          <div className="feefo__summary">
            <span>{items.length} review{items.length === 1 ? "" : "s"}</span>
            <span>avg {avg.toFixed(1)} ★</span>
          </div>
        ) : null}
      </header>

      {error ? (
        <p className="leads__error" role="alert" onClick={() => setError(undefined)}>
          {error}
        </p>
      ) : null}

      {items === null ? (
        <div className="feefo__loading">
          <Spinner label="Loading feedback…" />
        </div>
      ) : items.length === 0 ? (
        <p className="feefo__empty">No feedback received yet.</p>
      ) : (
        <div className="feefo__list">
          {items.map((r) => (
            <FeefoRequestRow
              key={r.id}
              r={r}
              hideStatus
              header={
                <Link to={`/staff/jobs/${r.job_id}`} className="feefo__job-link">
                  <span className="numeric">{r.job_number}</span> · {r.job_name || "Untitled job"}
                </Link>
              }
            />
          ))}
        </div>
      )}

      {showNew ? (
        <FeedbackRequestFlow
          onClose={() => setShowNew(false)}
          onCreated={() => {
            setShowNew(false);
            showToast("Feedback request sent.");
            load();
          }}
        />
      ) : null}

      <Toast toast={toast} />
    </section>
  );
}
